import { ReactNode, RefObject, useMemo } from 'react';
import { FormRenderProps } from 'react-final-form';

import { ProfKindCodesEnum } from 'APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { KnmPmMenu } from 'KnmPmPackage/KnmPmMenuModule/components/KnmPmMenu';
import { PmMenuItemsIdsEnum } from 'KnmPmPackage/KnmPmMenuModule/enums/PmMenuItemsIdsEnum';

import { getIsPmPaperlessActEnabled } from '../common/getIsPmPaperlessActEnabled';
import { IKnmPmForm } from '../interfaces/IKnmPmForm';
import { KnmPmContent } from './KnmPmContent';
import { IKnmForm } from '../../../KnmPackage/MainModule/interfaces/IKnmForm';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { useKnmPmFormStatusHintInit } from '../hooks/useKnmPmFormStatusHintInit';

export interface IKnmPmFormRenderProps {
  bodyRef: RefObject<HTMLDivElement>;
  knmData?: IKnm;
  lateChangesFieldLinks: Map<string, string>;
  renderForm: (menu: ReactNode, content: ReactNode) => ReactNode;
  values: IKnmPmForm;
  touched: FormRenderProps<IKnmForm>['touched'];
  settings: IAdminSetting[];
}

export const KnmPmFormRender = ({
  bodyRef,
  knmData,
  lateChangesFieldLinks,
  renderForm,
  values,
  touched,
  settings,
}: IKnmPmFormRenderProps) => {
  useKnmPmFormStatusHintInit({ values, touched, settings });

  const pmTypeCode = values.pmType?.code;
  const isPmWarningVisible = pmTypeCode === ProfKindCodesEnum.OP;
  const isPmHasNotificationVisible = pmTypeCode === ProfKindCodesEnum.PP;
  const isPmHasRejectVisible = pmTypeCode === ProfKindCodesEnum.PP;
  const isPmResultOfInspectionVisible = pmTypeCode === ProfKindCodesEnum.PP;
  const isPmRejectVisible = pmTypeCode === ProfKindCodesEnum.PP;
  const isPmEventsVisible = pmTypeCode === ProfKindCodesEnum.PP;

  const { isUnableToRefuse, reasonsPm } = values || {};

  // Блок акта живет только в безбумажном режиме, в остальных случаях
  // на его месте рендерится старый блок результатов ПМ.
  const isPaperlessActEnabled = getIsPmPaperlessActEnabled(values?.created, settings);

  const hiddenPmMenuItemsIds: PmMenuItemsIdsEnum[] = useMemo(() => {
    const newHiddenMenuItemsIds: PmMenuItemsIdsEnum[] = [];

    if (!isPmWarningVisible) {
      newHiddenMenuItemsIds.push(PmMenuItemsIdsEnum.PM_WARNING);
    }
    if (!isPmHasRejectVisible) {
      newHiddenMenuItemsIds.push(PmMenuItemsIdsEnum.PM_IS_REJECT_SUBJECT);
    }
    if (!isPmResultOfInspectionVisible) {
      newHiddenMenuItemsIds.push(PmMenuItemsIdsEnum.PM_RESULT_OF_INSPECTION);
    }
    if (!isPmRejectVisible) {
      newHiddenMenuItemsIds.push(PmMenuItemsIdsEnum.PM_REJECT);
    }
    if (!isPmEventsVisible) {
      newHiddenMenuItemsIds.push(PmMenuItemsIdsEnum.PM_EVENTS);
    }

    if (reasonsPm && reasonsPm.length > 0 && reasonsPm.some(item => Boolean(item.type))) {
      if (isUnableToRefuse && isPaperlessActEnabled) {
        newHiddenMenuItemsIds.push(PmMenuItemsIdsEnum.PM_RESULT_OF_INSPECTION);
      } else {
        newHiddenMenuItemsIds.push(PmMenuItemsIdsEnum.PM_ACT);
      }
    } else {
      newHiddenMenuItemsIds.push(
        PmMenuItemsIdsEnum.PM_ACT,
        PmMenuItemsIdsEnum.PM_RESULT_OF_INSPECTION,
        PmMenuItemsIdsEnum.PM_EVENTS,
      );
    }

    return newHiddenMenuItemsIds;
  }, [
    isPmWarningVisible,
    isPmHasNotificationVisible,
    isPmHasRejectVisible,
    isPmResultOfInspectionVisible,
    isUnableToRefuse,
    isPaperlessActEnabled,
    reasonsPm,
  ]);

  // Меню строится прямо в рендере, как в ЕРКНМ. Раньше оно кешировалось в state
  // и застывало на первом рендере, когда значения формы еще не пришли.
  const menu = (
    <KnmPmMenu dataRef={bodyRef} hiddenItemsIds={hiddenPmMenuItemsIds} lateChangesFieldLinks={lateChangesFieldLinks} />
  );

  const content = <KnmPmContent knmData={knmData} values={values} />;

  return <>{renderForm(menu, content)}</>;
};
